import React, { useState, useEffect } from 'react';
import {
    AppBar,
    Toolbar,
    Button,
    Box,
    Container,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Menu,
    MenuItem,
    Typography,
    Collapse,
    useTheme,
    styled,
    keyframes,
    Fab,
    // Avatar
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
// import PersonIcon from '@mui/icons-material/Person';
import FlavrapogiLogo from "../images/Flavrapogi-Logo.png";
import {
    Info as AboutIcon,
    Restaurant as ServicesIcon,
    Groups as ClientsIcon,
    VolunteerActivism as CSRIcon,
    Work as CareersIcon,
    Home as HomeIcon,
    ExpandMore,
    ExpandLess,
    Message as MessageIcon,
    ContactSupport as ContactIcon,
    Build as InstallIcon
} from '@mui/icons-material';
// import { auth } from '../firebase';
// import { onAuthStateChanged } from 'firebase/auth';

const pulseLogo = keyframes`
    0% { transform: scale(1); filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.21)) brightness(1.0) contrast(1.1); }
    50% { transform: scale(1.05); filter: drop-shadow(2px 2px 6px rgba(0,0,0,0.3)) brightness(1.1) contrast(1.2); }
    100% { transform: scale(1); filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.21)) brightness(1.0) contrast(1.1); }
`;

const StyledAppBar = styled(AppBar)(({ theme, isHomePage, scrolled, textColor }) => ({
    position: 'fixed',
    elevation: scrolled ? 4 : 0,
    backgroundColor: scrolled ? 'rgba(0, 0, 0, 0.6)' : 'transparent',
    color: textColor,
    boxShadow: scrolled ? theme.shadows[4] : 'none',
    transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
    backdropFilter: scrolled ? 'blur(10px)' : 'none',
    transform: 'translateZ(0)',
    ...(isHomePage && {
        // Specific styles for the home page can be added here
    }),
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
}));

const LogoLink = styled(Link)({
    textDecoration: 'none',
    '& img': {
        height: '60px',
        width: 'auto',
        position: 'fixed',
        top: '10px',
        left: '20px',
        zIndex: '1000',
        filter: 'drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.21)) brightness(1.0) contrast(1.1)',
        transition: 'all 0.3s ease',
        animation: `${pulseLogo} 3s infinite alternate`,
        '&:hover': {
            filter: 'drop-shadow(2px 2px 6px rgba(0,0,0,0.3)) brightness(1.1) contrast(1.2)',
        },
    },
});

const DesktopNavBox = styled(Box)(({ theme }) => ({
    display: 'none',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
        display: 'flex',
    },
}));

const MobileMenuButton = styled(IconButton)(({ theme, textColor }) => ({
    color: textColor,
    [theme.breakpoints.up('md')]: {
        display: 'none',
    },
}));

const HeaderButton = styled(Button)(({ theme, textColor }) => ({
    color: textColor,
    textTransform: 'none',
    fontSize: '1.0rem',
    fontWeight: 500,
    '&:hover': {
        opacity: 0.8,
    },
}));

const SubMenuItem = styled(MenuItem)(({ theme }) => ({
    color: theme.palette.text.primary,
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const MobileListItem = styled(ListItem)(({ theme, textColor }) => ({
    color: textColor,
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const FloatingContactButton = styled(Fab)(({ theme }) => ({
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    zIndex: 1500,
    '&:hover': {
        backgroundColor: theme.palette.secondary.dark,
    },
}));

const Header = () => {
    const theme = useTheme();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [mobileServiceOpen, setMobileServiceOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [pageBackgroundColor, setPageBackgroundColor] = useState('');
    // const [user, setUser] = useState(null);
    const isHomePage = location.pathname === '/';

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 50) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        const getBackgroundColor = () => {
            const bodyStyles = window.getComputedStyle(document.body);
            setPageBackgroundColor(bodyStyles.backgroundColor);
        };

        // const unsubscribe = onAuthStateChanged(auth, (user) => {
        //     setUser(user);
        // });

        getBackgroundColor();
        window.addEventListener('scroll', handleScroll);
        window.addEventListener('load', getBackgroundColor);
        const observer = new MutationObserver(getBackgroundColor);
        observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });

        return () => {
            // unsubscribe();
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('load', getBackgroundColor);
            observer.disconnect();
        };
    }, []);

    const isLightBackground = pageBackgroundColor.includes('rgb(255, 255, 255)') || pageBackgroundColor.includes('white');
    const headerTextColor = isLightBackground ? theme.palette.text.primary : theme.palette.common.white;
    const mobileTextColor = theme.palette.text.primary;

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleServicesClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleServicesClose = () => {
        setAnchorEl(null);
    };

    const handleMobileServiceToggle = () => {
        setMobileServiceOpen(!mobileServiceOpen);
    };

    const serviceItems = [
        { text: 'Industrial Catering', path: '/services/industrial' },
        { text: 'Educational Institutions & Executive Catering', path: '/services/educational' },
        { text: 'Healthcare Catering', path: '/services/healthcare' },
        { text: 'Retail Food Services', path: '/services/retail' },
        { text: 'Gallery', path: '/services/gallery' }
    ];

    const navItems = [
        { text: 'Home', path: '/', icon: <HomeIcon /> },
        { text: 'About Us', path: '/about', icon: <AboutIcon /> },
        {
            text: 'Services',
            path: '/services',
            icon: <ServicesIcon />,
            subItems: serviceItems
        },
        { text: 'Installation', path: '/install', icon: <InstallIcon /> },
        { text: 'Our Clients', path: '/clients', icon: <ClientsIcon /> },
        { text: 'CSR', path: '/csr', icon: <CSRIcon /> },
        { text: 'Careers', path: '/careers', icon: <CareersIcon /> }
    ];

    const drawer = (
        <Box sx={{ width: 250 }} role="presentation">
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                <IconButton onClick={handleDrawerToggle} sx={{ color: mobileTextColor }}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Divider />
            <List>
                {navItems.map((item) => (
                    <React.Fragment key={item.text}>
                        {item.subItems ? (
                            <>
                                <MobileListItem button onClick={item.text === 'Services' ? handleMobileServiceToggle : null} textColor={mobileTextColor}>
                                    <ListItemIcon sx={{ minWidth: 40, color: mobileTextColor }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText primary={item.text} primaryTypographyProps={{ color: mobileTextColor }} />
                                    {mobileServiceOpen ? <ExpandLess sx={{ color: mobileTextColor }} /> : <ExpandMore sx={{ color: mobileTextColor }} />}
                                </MobileListItem>
                                <Collapse in={mobileServiceOpen} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {item.subItems.map((subItem) => (
                                            <MobileListItem
                                                button
                                                key={subItem.text}
                                                component={Link}
                                                to={subItem.path}
                                                onClick={handleDrawerToggle}
                                                sx={{ pl: 4 }}
                                                textColor={mobileTextColor}
                                            >
                                                <ListItemText primary={subItem.text} primaryTypographyProps={{ color: mobileTextColor }} />
                                            </MobileListItem>
                                        ))}
                                    </List>
                                </Collapse>
                            </>
                        ) : (
                            <MobileListItem
                                button
                                component={Link}
                                to={item.path}
                                onClick={handleDrawerToggle}
                                textColor={mobileTextColor}
                            >
                                <ListItemIcon sx={{ minWidth: 40, color: mobileTextColor }}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText primary={item.text} primaryTypographyProps={{ color: mobileTextColor }} />
                            </MobileListItem>
                        )}
                    </React.Fragment>
                ))}
                <Divider />
                <MobileListItem button component={Link} to="/contact" onClick={handleDrawerToggle} textColor={mobileTextColor}>
                    <ListItemIcon sx={{ minWidth: 40, color: mobileTextColor }}>
                        <ContactIcon />
                    </ListItemIcon>
                    <ListItemText primary="Contact Us" primaryTypographyProps={{ color: mobileTextColor }} />
                </MobileListItem>
                {/* {user && (
                    <>
                        <Divider />
                        <MobileListItem button component={Link} to="/contact" onClick={handleDrawerToggle} textColor={mobileTextColor}>
                            <ListItemIcon sx={{ minWidth: 40, color: mobileTextColor }}>
                                <Avatar 
                                    src={user.photoURL} 
                                    sx={{ 
                                        width: 24, 
                                        height: 24,
                                        bgcolor: theme.palette.primary.main
                                    }}
                                >
                                    {user.displayName?.charAt(0) || user.email?.charAt(0)}
                                </Avatar>
                            </ListItemIcon>
                            <ListItemText 
                                primary={user.displayName || user.email || 'Account'} 
                                primaryTypographyProps={{ color: mobileTextColor }} 
                            />
                        </MobileListItem>
                    </>
                )} */}
            </List>
        </Box>
    );

    return (
        <>
            <StyledAppBar position="fixed" isHomePage={isHomePage} scrolled={scrolled} textColor={headerTextColor}>
                <Container maxWidth="lg">
                    <StyledToolbar>
                        <Box sx={{ flexGrow: 1 }}>
                            <LogoLink to="/">
                                <img
                                    src={FlavrapogiLogo}
                                    alt="Flavrapogi Logo"
                                />
                            </LogoLink>
                        </Box>

                        <DesktopNavBox>
                            {navItems.map((item) => (
                                item.subItems ? (
                                    <React.Fragment key={item.text}>
                                        <HeaderButton
                                            color="inherit"
                                            onClick={handleServicesClick}
                                            startIcon={item.icon}
                                            textColor={headerTextColor}
                                        >
                                            {item.text}
                                        </HeaderButton>
                                        <Menu
                                            anchorEl={anchorEl}
                                            open={Boolean(anchorEl)}
                                            onClose={handleServicesClose}
                                            sx={{ mt: 1 }}
                                        >
                                            {item.subItems.map((subItem) => (
                                                <SubMenuItem
                                                    key={subItem.text}
                                                    component={Link}
                                                    to={subItem.path}
                                                    onClick={handleServicesClose}
                                                >
                                                    <Typography variant="body1">{subItem.text}</Typography>
                                                </SubMenuItem>
                                            ))}
                                        </Menu>
                                    </React.Fragment>
                                ) : (
                                    <HeaderButton
                                        key={item.text}
                                        color="inherit"
                                        component={Link}
                                        to={item.path}
                                        startIcon={item.icon}
                                        textColor={headerTextColor}
                                    >
                                        {item.text}
                                    </HeaderButton>
                                )
                            ))}
                        </DesktopNavBox>

                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                            <IconButton 
                                component={Link}
                                to="/contact"
                                sx={{ 
                                    color: headerTextColor,
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                    }
                                }}
                            >
                                {/* {user ? (
                                    <Avatar 
                                        src={user.photoURL} 
                                        sx={{ 
                                            width: 36, 
                                            height: 36,
                                            bgcolor: theme.palette.primary.main
                                        }}
                                    >
                                        {user.displayName?.charAt(0) || user.email?.charAt(0)}
                                    </Avatar>
                                ) : (
                                    <PersonIcon fontSize="medium" />
                                )} */}
                            </IconButton>
                        </Box>

                        <MobileMenuButton
                            aria-label="open menu"
                            edge="end"
                            onClick={handleDrawerToggle}
                            textColor={headerTextColor}
                        >
                            <MenuIcon />
                        </MobileMenuButton>
                    </StyledToolbar>
                </Container>
            </StyledAppBar>

            <FloatingContactButton
                component={Link}
                to="/contact"
                aria-label="contact"
            >
                <MessageIcon />
            </FloatingContactButton>

            <Drawer
                anchor="right"
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true,
                }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
                }}
            >
                {drawer}
            </Drawer>
        </>
    );
};

export default Header;